import { useCallback, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

function defaultResolveRowId(row, index) {
  if (row.inventoryUuid) {
    return String(row.inventoryUuid);
  }

  return String(row.id ?? row.uuid ?? `row-${index}`);
}

function buildDefaultExpanded(data, resolveRowId, enableExpanding, defaultExpanded) {
  if (!enableExpanding || !defaultExpanded) {
    return {};
  }

  return data.reduce((acc, row, index) => {
    acc[resolveRowId(row, index)] = true;
    return acc;
  }, {});
}

function useDetailTable(data = [], columns = [], options = {}) {
  const {
    isLoading = false,
    enableRowSelection = false,
    enableMultiSelection = false,
    enableExpanding = false,
    getSubRows,
    getRowId: getRowIdOption,
    defaultExpanded = true,
    initialRowSelection = {},
  } = options;

  const resolveRowId = useCallback(
    (row, index) => (
      getRowIdOption ? getRowIdOption(row, index) : defaultResolveRowId(row, index)
    ),
    [getRowIdOption],
  );

  const [rowSelection, setRowSelection] = useState(initialRowSelection);
  const [columnSizing, setColumnSizing] = useState({});
  const [expanded, setExpanded] = useState(() => (
    buildDefaultExpanded(data, resolveRowId, enableExpanding, defaultExpanded)
  ));

  const handleRowSelectionChangeSingle = useCallback((updater) => {
    setRowSelection((old) => {
      const newSelection = typeof updater === 'function' ? updater(old) : updater;
      const selectedKeys = Object.keys(newSelection).filter((key) => newSelection[key]);

      if (selectedKeys.length > 1) {
        const lastSelectedKey = selectedKeys.find((key) => !old[key])
          || selectedKeys[selectedKeys.length - 1];
        return { [lastSelectedKey]: true };
      }

      return newSelection;
    });
  }, []);

  const handleRowSelectionChangeMulti = useCallback((updater) => {
    setRowSelection((old) => (typeof updater === 'function' ? updater(old) : updater));
  }, []);

  const handleRowSelectionChange = enableMultiSelection
    ? handleRowSelectionChangeMulti
    : handleRowSelectionChangeSingle;

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 48,
      maxSize: 640,
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      columnSizing,
      ...(enableRowSelection && { rowSelection }),
      ...(enableExpanding && { expanded }),
    },
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    onExpandedChange: enableExpanding ? setExpanded : undefined,
    getRowId: resolveRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enableExpanding && {
      getExpandedRowModel: getExpandedRowModel(),
      getSubRows: getSubRows ?? ((row) => row.subRows ?? []),
      enableExpanding: true,
      enableSubRowSelection: enableRowSelection && enableMultiSelection,
    }),
    enableRowSelection,
    enableMultiRowSelection: enableMultiSelection,
    enableSorting: true,
    enableSortingRemoval: true,
  });

  const getSelectedRow = useCallback(() => {
    if (!enableRowSelection) {
      return null;
    }

    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length === 0) {
      return null;
    }

    return selectedRows[0]?.original ?? null;
  }, [enableRowSelection, table]);

  const getSelectedRows = useCallback(() => {
    if (!enableRowSelection) {
      return [];
    }

    return table.getSelectedRowModel().rows.map((row) => row.original);
  }, [enableRowSelection, table]);

  const clearSelection = useCallback(() => {
    if (enableRowSelection) {
      setRowSelection({});
    }
  }, [enableRowSelection]);

  return useMemo(() => ({
    table,
    isLoading,
    columnSizing,
    selectedCount: enableRowSelection ? table.getSelectedRowModel().rows.length : 0,
    ...(enableRowSelection && {
      rowSelection,
      setRowSelection,
      getSelectedRow,
      ...(enableMultiSelection && { getSelectedRows }),
      clearSelection,
    }),
    ...(enableExpanding && {
      expanded,
      setExpanded,
    }),
  }), [
    table,
    isLoading,
    columnSizing,
    enableRowSelection,
    enableMultiSelection,
    enableExpanding,
    rowSelection,
    getSelectedRow,
    getSelectedRows,
    clearSelection,
    expanded,
  ]);
}

export default useDetailTable;
